import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUiStore } from '../stores/uiStore';
import { useBinderStore, type BinderWord, type ContentStatus } from '../stores/binderStore';
import MoveWordDialog from './MoveWordDialog';
import AddWordBox from './AddWordBox';
import Spinner from './Spinner';
import { useRunStore } from '../stores/runStore';
import { useTestBenchStore } from '../stores/testBenchStore';
import { copyText } from '../utils/clipboard';
import {
  FILTERS, SORTS, matches, nextText, readView, stepText, view, writeView,
  type BinderFilter, type BinderSort,
} from '../utils/binderView';
import './WordBinder.css';

// Word Binder side panel (Chunk W10, 2026-09-26): reads D1 only.
// Game Words and Social only words, both only when approved in the Word Bank
// (GET /api/binder). Picking a word opens it on the Content tab. The calendar
// lives on the Schedule tab (Chunk W7); the Binder is list only. "+ New Word"
// is the one-step Add word (2026-10-22, POST /api/wordbank/add-one): word and
// bucket, saved approved and scored. The Related rows under Game Words were
// removed 2026-10-22. Nothing here calls Airtable.
// Filters, sorts and Next to review (2026-10-22, Odin's ruling: the Binder
// points at the next thing to do): one filter at a time plus the search, a
// count beside each, and each row's progress, next stage, date, difficulty and
// a red mark when anything needs fixes. Default Waiting on me, closest to
// finished first; the last filter and sort are kept in this browser.
// Decluttered 2026-10-22 (Odin's ruling): Show and Order menus, and a row's
// next stage moved into the progress count's tooltip so the word shows in full.

type BinderTab = 'game' | 'social';

const STATUS_LABEL: Record<ContentStatus, string> = {
  none: 'no content',
  generated: 'generated',
  needs_fixes: 'needs fixes',
  approved: 'content approved',
  missing: 'missing',
};

// Small content mark next to each word: a dot for the status, then progress
// ("3/7", or "done"); hovering the count shows the next stage ("next: 3 Reveals").
// A word with a model run going shows a spinner in place of the dot (2026-10-22).
function ContentMark({ word }: { word: BinderWord }) {
  const running = useRunStore((s) => s.runs.find((r) => r.wordId === word.id));
  const g = word.guide;
  const step = stepText(word);
  const next = nextText(word);
  const title = running
    ? `${running.label} running`
    : `${STATUS_LABEL[word.status] || word.status}${g ? `, ${g.done} of ${g.total} stages done` : ''}`;
  return (
    <span className={`binder-mark binder-mark-${word.status}`} title={title} aria-label={title}>
      {running ? <Spinner label={title} /> : <span className="binder-mark-dot" />}
      {step && <span className="binder-mark-count" title={next || undefined}>{step}</span>}
    </span>
  );
}

interface MenuTarget { id: string; word: string; bucket: string }

export default function WordBinder() {
  const { sidebarOpen, setPage, contentOpenId, openContentWord } = useUiStore();
  const addStagedWord = useTestBenchStore((s) => s.addStagedWord);
  const { game, social, loading, error, fetchBinder } = useBinderStore();

  const [tab, setTab] = useState<BinderTab>('game');
  const [search, setSearch] = useState('');

  // Filter and sort, remembered in this browser.
  const [saved] = useState(readView);
  const [filter, setFilter] = useState<BinderFilter>(saved.filter);
  const [sort, setSort] = useState<BinderSort>(saved.sort);
  useEffect(() => { writeView({ filter, sort }); }, [filter, sort]);

  // "+ New Word": the one-step Add word box.
  const [newOpen, setNewOpen] = useState(false);

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; target: MenuTarget } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);
  const [moveTarget, setMoveTarget] = useState<MenuTarget | null>(null);

  useEffect(() => {
    fetchBinder();
  }, [fetchBinder]);

  // Click-outside + Escape dismiss for the context menu.
  useEffect(() => {
    if (!contextMenu) return;
    const onPointerDown = (ev: MouseEvent) => {
      const node = contextMenuRef.current;
      if (node && ev.target instanceof Node && node.contains(ev.target)) return;
      setContextMenu(null);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setContextMenu(null);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  const q = search.trim().toLowerCase();
  const tabFilters = useMemo(() => FILTERS.filter((f) => tab === 'game' || !f.gameOnly), [tab]);
  // Scheduled and Played are Game only; on the Social tab they read as Waiting on me.
  const activeFilter: BinderFilter = tabFilters.some((f) => f.id === filter) ? filter : 'waiting';
  const searchedGames = useMemo(() => game.filter((g) => !q || g.word.toLowerCase().includes(q)), [game, q]);
  const searchedSocials = useMemo(() => social.filter((s) => !q || s.word.toLowerCase().includes(q)), [social, q]);
  const base: BinderWord[] = tab === 'game' ? searchedGames : searchedSocials;
  const counts = useMemo(
    () => Object.fromEntries(tabFilters.map((f) => [f.id, base.filter((w) => matches(w, f.id)).length])),
    [tabFilters, base]
  );
  const games = useMemo(() => view(searchedGames, activeFilter, sort), [searchedGames, activeFilter, sort]);
  const socials = useMemo(() => view(searchedSocials, activeFilter, sort), [searchedSocials, activeFilter, sort]);

  // Next to review: the first Waiting on me word in the current sort (the one
  // after it when that word is already open).
  const waitingList = useMemo(() => view(base, 'waiting', sort), [base, sort]);
  const nextToReview = () => {
    const i = waitingList.findIndex((w) => w.id === contentOpenId);
    const pick = i >= 0 ? waitingList[i + 1] || waitingList[0] : waitingList[0];
    if (pick) openContentWord(pick.id);
  };
  const filterLabel = FILTERS.find((f) => f.id === activeFilter)?.label || '';

  const openMenu = useCallback((e: React.MouseEvent, target: MenuTarget) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, target });
  }, []);

  if (!sidebarOpen) return null;

  const renderRow = (w: BinderWord, bucket: string, extra?: React.ReactNode) => (
    <BinderRow
      key={`w-${w.id}`}
      word={w}
      selected={contentOpenId === w.id}
      extra={extra}
      onOpen={() => openContentWord(w.id)}
      onContextMenu={(e) => openMenu(e, { id: w.id, word: w.word, bucket })}
    />
  );

  return (
    <aside className="word-binder">
      <div className="binder-header">
        <h2 className="binder-title">Word Binder</h2>
        <div className="binder-controls">
          <div className="binder-mode-toggle" role="tablist" aria-label="Word type">
            <button role="tab" aria-selected={tab === 'game'} className={`binder-mode-btn ${tab === 'game' ? 'active' : ''}`} onClick={() => setTab('game')}>
              Game
            </button>
            <button role="tab" aria-selected={tab === 'social'} className={`binder-mode-btn ${tab === 'social' ? 'active' : ''}`} onClick={() => setTab('social')}>
              Social
            </button>
          </div>
        </div>
      </div>

      <div className="binder-search">
        <input
          type="text"
          placeholder={tab === 'game' ? 'Search Game Words...' : 'Search Social only words...'}
          className="binder-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="binder-filters binder-view">
        <label className="binder-view-row">
          <span className="binder-view-label">Show</span>
          <select
            className="binder-view-select"
            value={activeFilter}
            onChange={(e) => {
              const f = e.target.value as BinderFilter;
              setFilter(f);
              if (f === 'scheduled') setSort('date');
            }}
          >
            {tabFilters.map((f) => (
              <option key={f.id} value={f.id}>{f.label} ({counts[f.id] ?? 0})</option>
            ))}
          </select>
        </label>
        <label className="binder-view-row">
          <span className="binder-view-label">Order</span>
          <select
            className="binder-view-select"
            value={activeFilter === 'scheduled' ? 'date' : sort}
            disabled={activeFilter === 'scheduled'}
            title={activeFilter === 'scheduled' ? 'Scheduled is always in date order' : undefined}
            onChange={(e) => setSort(e.target.value as BinderSort)}
          >
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <button
          type="button"
          className="binder-next-btn"
          onClick={nextToReview}
          disabled={waitingList.length === 0}
          title={waitingList.length ? `Open the next Waiting on me word (${waitingList.length})` : 'Nothing is waiting on you'}
        >
          Next to review{waitingList.length ? ` (${waitingList.length})` : ''}
        </button>
      </div>

      <div className="binder-new">
        {!newOpen ? (
          <button className="binder-new-btn" onClick={() => setNewOpen(true)}>
            + New Word
          </button>
        ) : (
          <div className="binder-new-form">
            <AddWordBox compact onChanged={fetchBinder} />
            <div className="binder-new-actions">
              <button type="button" className="binder-retry" onClick={() => setNewOpen(false)}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="binder-content">
        {loading && <div className="binder-status">Loading words...</div>}

        {error && (
          <div className="binder-error">
            <p>{error}</p>
            <button className="binder-retry" onClick={fetchBinder}>Retry</button>
          </div>
        )}

        {!loading && !error && tab === 'game' && games.length === 0 && (
          <div className="binder-empty">
            <p>{game.length === 0 ? 'No approved Game Words yet.' : `No Game Words${q ? ' matching the search' : ''} in ${filterLabel}.`}</p>
            {game.length === 0 && <p className="binder-empty-hint">Approve Game Words in the Word Bank to list them here.</p>}
          </div>
        )}

        {!loading && !error && tab === 'social' && socials.length === 0 && (
          <div className="binder-empty">
            <p>{social.length === 0 ? 'No approved Social only words yet.' : `No Social only words${q ? ' matching the search' : ''} in ${filterLabel}.`}</p>
            {social.length === 0 && <p className="binder-empty-hint">Approve Social only words in the Word Bank to list them here. They run the Game Word stages for social posts and never go on the calendar or publish.</p>}
          </div>
        )}

        {!loading && tab === 'game' && games.length > 0 && (
          <div className="binder-group-items">
            {games.map((g) => renderRow(g, 'Game', (g.game_date || g.played_on)
              ? <span className="word-date" title={g.played_on ? 'Played' : 'Scheduled'}>{g.played_on ? `played ${g.played_on}` : g.game_date}</span>
              : null))}
          </div>
        )}

        {!loading && tab === 'social' && socials.length > 0 && (
          <div className="binder-group-items">
            {socials.map((s) => renderRow(
              s,
              'Related',
              s.linked_to.length > 0 ? <span className="related-row-relation">under {s.linked_to.join(', ')}</span> : null
            ))}
          </div>
        )}
      </div>

      {contextMenu && (
        <div ref={contextMenuRef} className="binder-context-menu" style={{ top: contextMenu.y, left: contextMenu.x }} role="menu">
          <button type="button" className="binder-context-item" role="menuitem"
            onClick={() => { void copyText(contextMenu.target.word); setContextMenu(null); }}>
            Copy word
          </button>
          <button type="button" className="binder-context-item" role="menuitem"
            onClick={() => { setMoveTarget(contextMenu.target); setContextMenu(null); }}>
            Move to another bucket
          </button>
          <button type="button" className="binder-context-item" role="menuitem"
            onClick={() => { addStagedWord(contextMenu.target.word); setPage('testbench'); setContextMenu(null); }}>
            Add to Model Test
          </button>
        </div>
      )}

      {moveTarget && (
        <MoveWordDialog
          wordId={moveTarget.id}
          wordText={moveTarget.word}
          currentBucket={moveTarget.bucket}
          onClose={() => setMoveTarget(null)}
          onMoved={() => {
            setMoveTarget(null);
            fetchBinder();
          }}
        />
      )}
    </aside>
  );
}

interface BinderRowProps {
  word: BinderWord;
  selected: boolean;
  extra?: React.ReactNode;
  onOpen: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

function BinderRow({ word, selected, extra, onOpen, onContextMenu }: BinderRowProps) {
  const [copied, setCopied] = useState(false);

  // One-click copy that never opens the word.
  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      void copyText(word.word).then((ok) => {
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1000);
      });
    },
    [word.word]
  );

  return (
    <button
      className={`binder-word ${selected ? 'selected' : ''}`}
      onClick={onOpen}
      onContextMenu={onContextMenu}
    >
      <span className="word-name">
        {word.word}
        {!word.bank_approved && <span className="unverified-badge" title="Not approved in the Word Bank yet">unapproved</span>}
      </span>
      <div className="word-meta">
        {extra}
        {word.guide?.difficulty != null && (
          <span className="binder-diff" title={`Difficulty ${word.guide.difficulty} of 5`}>D{word.guide.difficulty}</span>
        )}
        <ContentMark word={word} />
        {word.guide?.needs_fixes && <span className="binder-fix-mark" title="Needs fixes" aria-label="Needs fixes" />}
      </div>
      <span
        className={`binder-word-copy ${copied ? 'copied' : ''}`}
        role="button"
        tabIndex={-1}
        aria-label={`Copy "${word.word}"`}
        title="Copy word"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handleCopy}
      >
        {copied ? (
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2.5 7.5l3 3 6-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <rect x="4.5" y="4.5" width="7" height="7" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
            <path d="M2.5 9.5V3a1 1 0 0 1 1-1h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        )}
      </span>
    </button>
  );
}
